import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, message, Result, Spin, Steps } from "antd";
import {
  LockOutlined, CheckCircleOutlined, CloseCircleOutlined,
  BankOutlined, UserOutlined, TeamOutlined
} from "@ant-design/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../const";

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #014c75 0%, #00d9a9 100%);
  padding: 20px;
`;

const InviteCard = styled(Card)`
  width: 100%;
  max-width: 520px;
  border-radius: 14px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.22);

  .ant-card-body {
    padding: 40px;
  }

  .logo {
    text-align: center;
    margin-bottom: 28px;
    h1 {
      color: #014c75;
      font-size: 32px;
      font-weight: 700;
      margin: 0;
    }
    span {
      color: #00d9a9;
    }
  }

  .invite-info {
    text-align: center;
    margin-bottom: 28px;
    background: #f0f9ff;
    border-radius: 10px;
    padding: 18px 20px;
    border: 1px solid #bae6fd;

    h2 {
      color: #014c75;
      font-size: 18px;
      margin: 0 0 8px;
    }
    p {
      color: #555;
      font-size: 14px;
      margin: 0;
    }
    .customer-name {
      color: #00d9a9;
      font-weight: 700;
    }
  }
`;

const PartnerInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link — no token found.");
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await axios.get(
        `${API_CONST.PARTNER_RELATION_VALIDATE_TOKEN}/${token}`
      );
      setInvitation(res.data);
      form.setFieldsValue({ name: res.data.invitedName || "" });
    } catch (err) {
      setError(
        err.response?.data?.message || "This invitation link is invalid or has expired."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(API_CONST.PARTNER_RELATION_REGISTER_AND_ACCEPT, {
        token,
        password: values.password,
        organizationName: values.organizationName,
        name: values.name,
      });
      setSuccess(true);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Failed to create account. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <InviteCard>
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#666" }}>Validating your invitation…</p>
          </div>
        </InviteCard>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <InviteCard>
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
            title="Invitation Not Found"
            subTitle={error}
            extra={
              <Button
                type="primary"
                style={{ background: "#014c75" }}
                onClick={() => navigate("/")}
              >
                Go to Login
              </Button>
            }
          />
        </InviteCard>
      </PageWrapper>
    );
  }

  if (success) {
    return (
      <PageWrapper>
        <InviteCard>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: "#00d9a9" }} />}
            title="You're all set!"
            subTitle={
              <div style={{ textAlign: "center" }}>
                <p style={{ marginBottom: 4 }}>
                  Your Partner account has been created and the invitation from{" "}
                  <strong style={{ color: "#014c75" }}>{invitation?.customerOrgName}</strong> has
                  been accepted.
                </p>
                <p style={{ color: "#888", fontSize: 13 }}>
                  Your account is pending admin approval. You can log in now — some features
                  will be available once your account is approved.
                </p>
              </div>
            }
            extra={
              <Button
                type="primary"
                size="large"
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
                onClick={() => navigate("/")}
              >
                Go to Login
              </Button>
            }
          />
        </InviteCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <InviteCard>
        <div className="logo">
          <h1>
            Hire<span>NXT</span>
          </h1>
        </div>

        <div className="invite-info">
          <TeamOutlined style={{ fontSize: 24, color: "#014c75", marginBottom: 8 }} />
          <h2>Partnership Invitation</h2>
          <p>
            <strong style={{ color: "#014c75" }}>{invitation?.customerOrgName}</strong> has
            invited you to connect as a partner on HireNXT.
          </p>
          {invitation?.invitedEmail && (
            <p style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
              Invitation sent to <strong>{invitation.invitedEmail}</strong>
            </p>
          )}
        </div>

        <p style={{ color: "#555", fontSize: 13, marginBottom: 20, textAlign: "center" }}>
          Create your Partner account below to accept this invitation.
        </p>

        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
          <Form.Item label="Your Email">
            <Input
              value={invitation?.invitedEmail}
              disabled
              style={{ background: "#f5f5f5", color: "#555" }}
            />
          </Form.Item>

          <Form.Item
            label="Your Full Name"
            name="name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Your full name" />
          </Form.Item>

          <Form.Item
            label="Partner Organization Name"
            name="organizationName"
            rules={[{ required: true, message: "Please enter your organization name" }]}
            extra="This will be your company name on the platform."
          >
            <Input prefix={<BankOutlined />} placeholder="e.g. Acme Technologies Pvt Ltd" />
          </Form.Item>

          <Form.Item
            label="Create Password"
            name="password"
            rules={[
              { required: true, message: "Please create a password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Choose a strong password" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Re-enter your password" />
          </Form.Item>

          <Form.Item style={{ marginTop: 28 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              style={{
                height: 48,
                background: "#00d9a9",
                borderColor: "#00d9a9",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Accept Invitation & Create Account
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <p style={{ color: "#aaa", fontSize: 12 }}>
            Already have an account?{" "}
            <a href="/" style={{ color: "#014c75" }}>
              Log in here
            </a>
          </p>
        </div>
      </InviteCard>
    </PageWrapper>
  );
};

export default PartnerInvitation;
